import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, RotateCcw, Save, ZoomIn, ZoomOut, Maximize2, Sparkles, ArrowDownToLine } from 'lucide-react';
import { toast } from 'sonner';
import { uploadReferenceItemPhoto, fetchReferenceItemPhotoBlobUrl, aiExtractReferenceDevice } from '../services/api';

/**
 * Re-crop a reference library item's photo. Lighter sibling of
 * `CertTrainFrameAdjuster` — same pan/zoom/bbox interaction model but
 * no AI re-run, no layout dependency: the crop is applied client-side
 * via a `<canvas>` and the resulting JPEG is POSTed back through the
 * existing item-photo upload route, replacing the stored bytes.
 *
 * Coord space: x / y / w / h are PERCENTAGES of the photo's natural
 * dimensions (so crop math is independent of viewport zoom).
 */
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const HANDLE_PX = 18;

const ReferenceItemPhotoCropper = ({ open, onOpenChange, itemId, onSaved, onApplyTags }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  const [imgDims, setImgDims] = useState(null); // {w, h}
  const [coords, setCoords] = useState({ x: 5, y: 5, w: 90, h: 90 });
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null); // {tags, isSpare}
  const [viewport, setViewport] = useState({ zoom: 1, panX: 0, panY: 0 });
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const dragRef = useRef(null);

  // Fetch the item's current photo as a blob URL (auth-protected).
  useEffect(() => {
    if (!open || !itemId) return;
    let cancelled = false;
    let blobUrl = null;
    setPhotoError(null);
    setPhotoUrl(null);
    setViewport({ zoom: 1, panX: 0, panY: 0 });
    setCoords({ x: 5, y: 5, w: 90, h: 90 });
    setAiResult(null);
    fetchReferenceItemPhotoBlobUrl(itemId).then(u => {
      if (cancelled) { URL.revokeObjectURL(u); return; }
      blobUrl = u;
      setPhotoUrl(u);
    }).catch(e => {
      if (!cancelled) setPhotoError(e?.message || 'Could not load photo');
    });
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [open, itemId]);

  // Read the photo's natural dimensions once it's painted.
  const onImgLoad = () => {
    const im = imgRef.current;
    if (im) setImgDims({ w: im.naturalWidth, h: im.naturalHeight });
  };

  // Container's rendered px box (object-contain so it fits the photo's AR).
  const rect = useMemo(() => {
    const c = containerRef.current;
    if (!c) return { w: 1, h: 1 };
    const r = c.getBoundingClientRect();
    return { w: r.width, h: r.height };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current, imgDims, viewport]);

  /* ----- bbox drag / resize ---------------------------------- */
  const onPointerDown = (e, handle) => {
    if (busy) return;
    e.target.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      handle, // 'move' | 'nw' | 'ne' | 'sw' | 'se'
      startX: e.clientX,
      startY: e.clientY,
      coords: { ...coords },
      zoom: viewport.zoom,
    };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const c = containerRef.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const dxPct = ((e.clientX - dragRef.current.startX) / r.width / dragRef.current.zoom) * 100;
    const dyPct = ((e.clientY - dragRef.current.startY) / r.height / dragRef.current.zoom) * 100;
    const o = dragRef.current.coords;
    let { x, y, w, h } = o;
    const handle = dragRef.current.handle;
    if (handle === 'move') {
      x = Math.max(0, Math.min(100 - w, o.x + dxPct));
      y = Math.max(0, Math.min(100 - h, o.y + dyPct));
    } else {
      // Anchor opposite corner; resize from the dragged corner.
      let x2 = o.x + o.w;
      let y2 = o.y + o.h;
      if (handle.includes('w')) x = Math.max(0, Math.min(x2 - 5, o.x + dxPct));
      if (handle.includes('e')) x2 = Math.max(x + 5, Math.min(100, o.x + o.w + dxPct));
      if (handle.includes('n')) y = Math.max(0, Math.min(y2 - 5, o.y + dyPct));
      if (handle.includes('s')) y2 = Math.max(y + 5, Math.min(100, o.y + o.h + dyPct));
      w = x2 - x;
      h = y2 - y;
    }
    setCoords({ x, y, w, h });
  };

  const onPointerUp = (e) => {
    e.target.releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  };

  /* ----- zoom ----------------------------------------------- */
  const setZoom = (next) => {
    setViewport(v => {
      const z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
      // Clamp pan so photo always fills container.
      const c = containerRef.current;
      if (!c) return { ...v, zoom: z };
      const r = c.getBoundingClientRect();
      const maxPanX = (r.width * (z - 1)) / 2;
      const maxPanY = (r.height * (z - 1)) / 2;
      return {
        zoom: z,
        panX: Math.max(-maxPanX, Math.min(maxPanX, v.panX)),
        panY: Math.max(-maxPanY, Math.min(maxPanY, v.panY)),
      };
    });
  };
  const onWheel = (e) => {
    e.preventDefault();
    const dir = e.deltaY < 0 ? 1 : -1;
    setZoom(viewport.zoom + dir * 0.2);
  };

  /* ----- live "what AI sees" preview ----------------------- */
  // Repaint the preview canvas whenever the bbox or photo changes,
  // so engineers can verify their crop boundary before re-running
  // AI or saving. Width is capped to keep the preview small (~280 px)
  // while preserving the crop's aspect ratio.
  useEffect(() => {
    const canvas = previewCanvasRef.current;
    const im = imgRef.current;
    if (!canvas || !im || !imgDims || !photoUrl) return;
    const sw = Math.max(1, Math.round((coords.w / 100) * imgDims.w));
    const sh = Math.max(1, Math.round((coords.h / 100) * imgDims.h));
    const sx = Math.round((coords.x / 100) * imgDims.w);
    const sy = Math.round((coords.y / 100) * imgDims.h);
    const targetW = 280;
    const scale = Math.min(1, targetW / sw);
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    try {
      ctx.drawImage(im, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    } catch {
      // Image might not be fully decoded yet — useEffect will retrigger.
    }
  }, [coords, imgDims, photoUrl, aiPanelOpen]);

  // Build a JPEG blob from the current crop bbox — shared between the
  // "Save crop" and "Re-run AI" actions.
  const buildCropBlob = async () => {
    if (!imgRef.current || !imgDims) return null;
    const im = imgRef.current;
    const sx = Math.round((coords.x / 100) * imgDims.w);
    const sy = Math.round((coords.y / 100) * imgDims.h);
    const sw = Math.max(1, Math.round((coords.w / 100) * imgDims.w));
    const sh = Math.max(1, Math.round((coords.h / 100) * imgDims.h));
    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(im, sx, sy, sw, sh, 0, 0, sw, sh);
    return await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
  };

  /* ----- AI re-run ----------------------------------------- */
  const rerunAi = async () => {
    setAiBusy(true);
    setAiResult(null);
    try {
      const blob = await buildCropBlob();
      if (!blob) throw new Error('Could not encode crop');
      const file = new File([blob], 'crop.jpg', { type: 'image/jpeg' });
      const res = await aiExtractReferenceDevice(file);
      setAiResult(res || { tags: {} });
      const t = res?.tags || {};
      const filled = Object.values(t).filter(v => v !== '' && v != null && v !== false).length;
      if (filled === 0) {
        toast.warning('AI could not read this crop — try tightening the bbox');
      } else {
        toast.success(`AI read ${filled} field${filled === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'AI re-run failed');
    } finally {
      setAiBusy(false);
    }
  };

  /* ----- save: client-side crop -> JPEG -> upload ----------- */
  const save = async () => {
    if (!imgRef.current || !imgDims) return;
    setBusy(true);
    try {
      const blob = await buildCropBlob();
      if (!blob) throw new Error('Could not encode crop');
      const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
      const updated = await uploadReferenceItemPhoto(itemId, file);
      toast.success('Photo cropped + saved');
      onSaved?.(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setCoords({ x: 5, y: 5, w: 90, h: 90 });
    setViewport({ zoom: 1, panX: 0, panY: 0 });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit photo crop</DialogTitle>
        </DialogHeader>

        {photoError ? (
          <div className="p-6 text-center text-red-600 text-sm">{photoError}</div>
        ) : !photoUrl ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin inline-block mr-2" />
            Loading photo…
          </div>
        ) : (
          <>
            <div
              ref={containerRef}
              className="relative bg-slate-900 rounded overflow-hidden touch-none select-none"
              style={{ aspectRatio: imgDims ? `${imgDims.w}/${imgDims.h}` : '4/3', maxHeight: '60vh' }}
              onWheel={onWheel}
              data-testid="ref-photo-cropper-canvas"
            >
              <div
                className="absolute inset-0"
                style={{
                  transform: `translate(${viewport.panX}px, ${viewport.panY}px) scale(${viewport.zoom})`,
                  transformOrigin: 'center center',
                }}
              >
                <img
                  ref={imgRef}
                  src={photoUrl}
                  alt=""
                  onLoad={onImgLoad}
                  className="w-full h-full object-contain pointer-events-none"
                  draggable={false}
                />
                {/* Crop bbox */}
                <div
                  className="absolute border-2 border-emerald-400 bg-emerald-400/10 cursor-move"
                  style={{
                    left: `${coords.x}%`, top: `${coords.y}%`,
                    width: `${coords.w}%`, height: `${coords.h}%`,
                    borderWidth: `${2 / viewport.zoom}px`,
                  }}
                  onPointerDown={(e) => onPointerDown(e, 'move')}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  data-testid="ref-photo-crop-bbox"
                >
                  {['nw', 'ne', 'sw', 'se'].map(h => (
                    <div
                      key={h}
                      className="absolute bg-emerald-500 border border-white shadow-md"
                      style={{
                        width: `${HANDLE_PX / viewport.zoom}px`,
                        height: `${HANDLE_PX / viewport.zoom}px`,
                        left: h.includes('w') ? `-${(HANDLE_PX / 2) / viewport.zoom}px` : 'auto',
                        right: h.includes('e') ? `-${(HANDLE_PX / 2) / viewport.zoom}px` : 'auto',
                        top: h.includes('n') ? `-${(HANDLE_PX / 2) / viewport.zoom}px` : 'auto',
                        bottom: h.includes('s') ? `-${(HANDLE_PX / 2) / viewport.zoom}px` : 'auto',
                        cursor: `${h}-resize`,
                      }}
                      onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e, h); }}
                      onPointerMove={onPointerMove}
                      onPointerUp={onPointerUp}
                      onPointerCancel={onPointerUp}
                    />
                  ))}
                </div>
              </div>

              {/* Zoom toolbar */}
              <div className="absolute right-2 bottom-2 flex flex-col gap-1 bg-white/90 rounded shadow p-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(viewport.zoom + 0.5)} disabled={viewport.zoom >= MAX_ZOOM} data-testid="ref-photo-zoom-in">
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
                <span className="text-[10px] text-center font-mono">{viewport.zoom.toFixed(1)}×</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(viewport.zoom - 0.5)} disabled={viewport.zoom <= MIN_ZOOM} data-testid="ref-photo-zoom-out">
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewport({ zoom: 1, panX: 0, panY: 0 })} data-testid="ref-photo-zoom-fit">
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="text-[11px] text-slate-500 font-mono mt-1">
              x={coords.x.toFixed(1)}% · y={coords.y.toFixed(1)}% · w={coords.w.toFixed(1)}% · h={coords.h.toFixed(1)}%
            </div>
          </>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setAiPanelOpen(true)}
            disabled={busy || !photoUrl}
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 mr-auto"
            data-testid="ref-photo-open-ai-panel"
          >
            <Sparkles className="h-3.5 w-3.5 mr-1" /> What AI sees
          </Button>
          <Button variant="outline" onClick={reset} disabled={busy || !photoUrl} data-testid="ref-photo-reset">
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy || !photoUrl} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ref-photo-save-crop">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save crop
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Nested AI panel — surfaces the live "what AI sees" canvas, the
          Re-run AI action and the parsed tag panel. Kept out of the
          main editor so the cropper itself stays compact on phones. */}
      <Dialog open={aiPanelOpen} onOpenChange={setAiPanelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>What the AI sees</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded border border-slate-200 bg-slate-50 p-2 flex items-center justify-center min-h-[120px]">
              <canvas
                ref={previewCanvasRef}
                className="max-w-full h-auto rounded shadow-sm"
                data-testid="ref-photo-ai-preview-canvas"
              />
            </div>
            <div className="text-[11px] text-slate-500 italic text-center">
              Live mirror of the green bbox — exactly what gets sent.
            </div>
            <Button
              variant="outline"
              onClick={rerunAi}
              disabled={aiBusy || busy}
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 w-full"
              data-testid="ref-photo-rerun-ai"
            >
              {aiBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Re-run AI
            </Button>
            {aiResult && (
              <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2.5 text-xs space-y-1" data-testid="ref-photo-ai-result">
                <div className="font-semibold text-emerald-800 mb-1.5 flex items-center justify-between">
                  AI guess
                  {onApplyTags && (
                    <button
                      type="button"
                      onClick={() => { onApplyTags(aiResult.tags || {}); toast.success('Applied to form'); setAiPanelOpen(false); }}
                      className="text-[10px] font-medium text-emerald-700 hover:text-emerald-900 underline-offset-2 hover:underline inline-flex items-center gap-0.5"
                      data-testid="ref-photo-ai-apply"
                    >
                      <ArrowDownToLine className="h-3 w-3" /> Apply to form
                    </button>
                  )}
                </div>
                {aiResult.isSpare ? (
                  <div className="italic text-amber-700">AI flagged this as a SPARE / blank slot.</div>
                ) : (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-0.5 font-mono">
                    <dt className="text-slate-500">BS EN</dt>
                    <dd>{aiResult.tags?.bsEn || <span className="text-slate-400">—</span>}</dd>
                    <dt className="text-slate-500">Type</dt>
                    <dd>{aiResult.tags?.mcbType || <span className="text-slate-400">—</span>}</dd>
                    <dt className="text-slate-500">Rating</dt>
                    <dd>{aiResult.tags?.ratingA != null ? `${aiResult.tags.ratingA}A` : <span className="text-slate-400">—</span>}</dd>
                    <dt className="text-slate-500">RCD mA</dt>
                    <dd>{aiResult.tags?.ratedOperatingMa != null ? `${aiResult.tags.ratedOperatingMa}mA` : <span className="text-slate-400">—</span>}</dd>
                    <dt className="text-slate-500">3-pole</dt>
                    <dd>{aiResult.tags?.isThreePole ? 'YES' : 'no'}</dd>
                  </dl>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiPanelOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ReferenceItemPhotoCropper;
